import React, { useState } from "react";
import { Link } from "react-router-dom";
import Auth from "../Auth";
import { useToast } from "../components/ToastWrapper";
import styled from "styled-components/macro";

function SharedDocumentsLookup() {
  const [documentUrl, setDocumentUrl] = useState("");
  const [documentId, setDocumentId] = useState("");
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { setToast } = useToast();

  const extractShortId = (url) => {
    const regex = /\/app\/documents\/([^/]+)\/feedback/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const handleUrlSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setDocument(null);

    const shortId = extractShortId(documentUrl);
    if (!shortId) {
      setError("Invalid URL format. Expected format: https://editor.sudowrite.com/app/documents/{shortId}/feedback");
      setLoading(false);
      return;
    }

    try {
      const response = await Auth.fetchWithIdToken(`/api/admin/shared-documents/shortId/${shortId}`);
      if (response.document) {
        setDocument(response.document);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setError(`No shared document found with shortId: ${shortId}`);
      } else {
        console.error("Error fetching document:", error);
        setError(`Error fetching document: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleIdSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setDocument(null);

    if (!documentId.trim()) {
      setError("Please enter a document ID");
      setLoading(false);
      return;
    }

    try {
      const response = await Auth.fetchWithIdToken(`/api/admin/shared-documents/documentId/${documentId}`);
      if (response.document) {
        setDocument(response.document);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setError(`No shared document found with document ID: ${documentId}`);
      } else {
        console.error("Error fetching document:", error);
        setError(`Error fetching document: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnpublish = async () => {
    if (!document) return;

    try {
      setLoading(true);
      await Auth.fetchWithIdToken(`/api/admin/shared-documents/${document.documentId}/unpublish`, {
        method: "POST",
      });

      // Update the document status locally
      setDocument({
        ...document,
        revokedAt: new Date().toISOString(),
      });

      setToast("Document successfully unpublished");
    } catch (error) {
      setError(`Error unpublishing document: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!document) return;

    try {
      setLoading(true);
      await Auth.fetchWithIdToken(`/api/admin/shared-documents/${document.documentId}/restore`, {
        method: "POST",
      });

      // Update the document status locally
      setDocument({
        ...document,
        revokedAt: null,
      });

      setToast("Document successfully restored");
    } catch (error) {
      setError(`Error restoring document: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <h2>Find Shared Document</h2>

      <SearchSection>
        <SearchGroup>
          <h3>Search by URL</h3>
          <form onSubmit={handleUrlSearch}>
            <StyledInput
              type="text"
              placeholder="https://editor.sudowrite.com/app/documents/abc-123/feedback"
              value={documentUrl}
              onChange={(e) => setDocumentUrl(e.target.value)}
            />
            <StyledButton
              type="submit"
              disabled={loading}
            >
              Search
            </StyledButton>
          </form>
        </SearchGroup>

        <SearchGroup top="20px">
          <h3>Search by Document ID</h3>
          <form onSubmit={handleIdSearch}>
            <StyledInput
              type="text"
              placeholder="1Ouz1cPsNgZROynD6po2"
              value={documentId}
              onChange={(e) => setDocumentId(e.target.value)}
            />
            <StyledButton
              type="submit"
              disabled={loading}
            >
              Search
            </StyledButton>
          </form>
        </SearchGroup>
      </SearchSection>

      {loading && <LoadingMessage>Loading...</LoadingMessage>}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      {document && (
        <DocumentDetails>
          <h3>Document Details</h3>
          <StyledTable>
            <tbody>
              <TableRow>
                <LabelCell>Title</LabelCell>
                <ValueCell>{document.title || "Untitled"}</ValueCell>
              </TableRow>
              <TableRow>
                <LabelCell>Short ID</LabelCell>
                <ValueCell>{document.shortId}</ValueCell>
              </TableRow>
              <TableRow>
                <LabelCell>Document ID</LabelCell>
                <ValueCell>{document.documentId}</ValueCell>
              </TableRow>
              <TableRow>
                <LabelCell>Author</LabelCell>
                <ValueCell>
                  <Link to={`/admin/user/${document.authorId}`}>{document.author || document.authorId}</Link>
                </ValueCell>
              </TableRow>
              <TableRow>
                <LabelCell>Published At</LabelCell>
                <ValueCell>{new Date(document.publishedAt).toLocaleString()}</ValueCell>
              </TableRow>
              <TableRow>
                <LabelCell>Status</LabelCell>
                <ValueCell>{document.revokedAt ? <StatusText>Unpublished on {new Date(document.revokedAt).toLocaleString()}</StatusText> : <StatusText isPublished>Published</StatusText>}</ValueCell>
              </TableRow>
              <TableRow>
                <LabelCell>Comments Enabled</LabelCell>
                <ValueCell>{document.commentsEnabled ? "Yes" : "No"}</ValueCell>
              </TableRow>
              <TableRow>
                <LabelCell>Feedback Link</LabelCell>
                <ValueCell>
                  <a
                    href={`${window.location.origin}/app/documents/${document.shortId}/feedback`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {`${window.location.origin}/app/documents/${document.shortId}/feedback`}
                  </a>
                </ValueCell>
              </TableRow>
              <TableRow noBorder>
                <LabelCell>Actions</LabelCell>
                <ValueCell>
                  {document.revokedAt ? (
                    <StyledButton
                      onClick={handleRestore}
                      disabled={loading}
                      primary
                    >
                      Restore
                    </StyledButton>
                  ) : (
                    <StyledButton
                      onClick={handleUnpublish}
                      disabled={loading}
                      danger
                    >
                      Unpublish
                    </StyledButton>
                  )}
                </ValueCell>
              </TableRow>
            </tbody>
          </StyledTable>
        </DocumentDetails>
      )}
    </Container>
  );
}

export default SharedDocumentsLookup;

// Styled Components
const Container = styled.div`
  margin-bottom: 30px;
`;

const SearchSection = styled.div`
  margin-bottom: 20px;
`;

const SearchGroup = styled.div`
  margin-top: ${(props) => (props.top ? props.top : "0")};
`;

const StyledInput = styled.input`
  width: 400px;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-size: 14px;
`;

const StyledButton = styled.button`
  margin-left: 10px;
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  background: ${(props) => (props.danger ? "#f44336" : props.primary ? "var(--primary-cta)" : "#666")};
  color: white;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const LoadingMessage = styled.div`
  margin: 20px 0;
`;

const ErrorMessage = styled.div`
  color: red;
  margin: 20px 0;
`;

const DocumentDetails = styled.div`
  margin-top: 30px;

  h3 {
    margin-bottom: 20px;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #ddd;
`;

const TableRow = styled.tr`
  border-bottom: ${(props) => (props.noBorder ? "none" : "1px solid #ddd")};
`;

const LabelCell = styled.td`
  padding: 12px 15px;
  font-weight: bold;
  width: 200px;
  background: #f8f8f8;
`;

const ValueCell = styled.td`
  padding: 12px 15px;
`;

const StatusText = styled.span`
  color: ${(props) => (props.isPublished ? "#4CAF50" : "#f44336")};
`;
